import {
	CircularProgress,
	Dialog,
	DialogContent,
	Divider,
	FormControl,
	Icon,
	InputLabel,
	MenuItem,
	Select
} from "@mui/material";
import MDBox from "components/Basics/MDBox";
import MDButton from "components/Basics/MDButton";
import MDTypography from "components/Basics/MDTypography";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import DeploymentActions from "redux-react/actions/deploymentActions";
import SettingsActions from "redux-react/actions/settingsActions";
import FormActions from "redux-react/actions/formAction";
import { socket } from "redux-react/middleware/ws";

/**
 * Migration progress steps, received via websocket event `faiq_migration_progress`.
 * Each event carries { step, label, status: "pending" | "running" | "done" | "error" }
 */
const MIGRATION_STEPS = [
	// { key: "init", label: "Initialisation" },
	// { key: "export_source", label: "Export source" },
	// { key: "transform", label: "Transformation" },
	// { key: "import_target", label: "Import cible" },
	// { key: "finalize", label: "Finalisation" }
	{ key: "done", label: "Terminé" }
];

const AccountAssistantPicker = ({
	label,
	clients,
	accountValue,
	assistantValue,
	onAccountChange,
	onAssistantChange,
	assistants,
	disabled
}) => (
	<MDBox display="flex" flexDirection="row" gap={1} flex={1}>
		<FormControl sx={{ flex: 1 }} disabled={disabled}>
			<InputLabel>{label} — Compte</InputLabel>
			<Select
				label={`${label} — Compte`}
				value={accountValue}
				onChange={e => onAccountChange(e.target.value)}
			>
				{clients.map((client, i) => (
					<MenuItem
						key={i}
						value={client.accountID}
						disabled={(client.assistants ?? []).length < 1}
					>
						{client.name ?? client.description ?? client.accountID}
					</MenuItem>
				))}
			</Select>
		</FormControl>
		<FormControl sx={{ flex: 1 }} disabled={disabled || !accountValue}>
			<InputLabel>{label} — Assistant</InputLabel>
			<Select
				label={`${label} — Assistant`}
				value={assistantValue}
				onChange={e => onAssistantChange(e.target.value)}
			>
				{assistants.map((a, i) => (
					<MenuItem key={i} value={a.assistantID}>
						{`${a.name} (${a.assistantID})`}
					</MenuItem>
				))}
			</Select>
		</FormControl>
	</MDBox>
);

const FAIQMigrationDialog = ({ open, onClose }) => {
	const dispatch = useDispatch();

	const [clients, setClients] = useState([]);

	// Source
	const [sourceAccount, setSourceAccount] = useState("");
	const [sourceAssistants, setSourceAssistants] = useState([]);
	const [sourceAssistant, setSourceAssistant] = useState("");

	// Target
	const [targetAccount, setTargetAccount] = useState("");
	const [targetAssistants, setTargetAssistants] = useState([]);
	const [targetAssistant, setTargetAssistant] = useState("");

	// Migration state
	const [migrating, setMigrating] = useState(false);
	const [stepStatuses, setStepStatuses] = useState({});
	const [migrationDone, setMigrationDone] = useState(false);
	const [migrationError, setMigrationError] = useState(null);

	// Active stepper step (UI)
	const [activeStep, setActiveStep] = useState(0);

	const resetState = () => {
		setSourceAccount("");
		setSourceAssistants([]);
		setSourceAssistant("");
		setTargetAccount("");
		setTargetAssistants([]);
		setTargetAssistant("");
		setMigrating(false);
		setStepStatuses({});
		setMigrationDone(false);
		setMigrationError(null);
		setActiveStep(0);
	};

	const handleClose = () => {
		resetState();
		onClose();
	};

	// Load clients on mount
	useEffect(() => {
		if (!open) return;
		dispatch(
			FormActions.getItemsFromCollection(
				"accountConfig",
				{ catalog: "legacy", sort: "accountID" },
				({ items }) => setClients(items)
			)
		);
	}, [open]);

	const loadAssistants = (accountID, setter) => {
		dispatch(DeploymentActions.getAssistantsForAccount(accountID, res => setter(res)));
	};

	// Websocket: listen for migration progress
	useEffect(() => {
		const onProgress = data => {
			const { step, status, label, error } = data;
			setStepStatuses(prev => ({ ...prev, [step]: { status, label } }));

			const stepIndex = MIGRATION_STEPS.findIndex(s => s.key === step);
			if (stepIndex !== -1 && status === "running") {
				setActiveStep(stepIndex);
			}
			if (status === "done" && step === MIGRATION_STEPS[MIGRATION_STEPS.length - 1].key) {
				setMigrationDone(true);
				setMigrating(false);
			}
			if (status === "error") {
				setMigrationError(error ?? `Erreur à l'étape "${label ?? step}"`);
				setMigrating(false);
			}
		};

		socket.on("faiq_migration_progress", onProgress);
		return () => socket.off("faiq_migration_progress", onProgress);
	}, []);

	const launchMigration = () => {
		setMigrating(true);
		setStepStatuses({});
		setMigrationDone(false);
		setMigrationError(null);
		setActiveStep(0);

		dispatch(
			SettingsActions.migrateFAIQ(
				{
					source: { accountID: sourceAccount, assistantID: sourceAssistant },
					target: { accountID: targetAccount, assistantID: targetAssistant },
					version: "3.0"
				},
				() => {},
				err => {
					setMigrationError(
						err?.response?.data?.error?.message ?? "Erreur lors du lancement de la migration"
					);
					setMigrating(false);
				}
			)
		);
	};

	const canLaunch =
		sourceAccount && sourceAssistant && targetAccount && targetAssistant && !migrating;

	const getStepIcon = stepKey => {
		const s = stepStatuses[stepKey];
		if (!s) return null;
		if (s.status === "running") return <CircularProgress size={16} color="info" />;
		if (s.status === "done")
			return (
				<Icon color="success" sx={{ fontSize: 18 }}>
					check_circle
				</Icon>
			);
		if (s.status === "error")
			return (
				<Icon color="error" sx={{ fontSize: 18 }}>
					error
				</Icon>
			);
		return null;
	};

	return (
		<Dialog fullWidth maxWidth="md" open={open} onClose={migrating ? undefined : handleClose}>
			{/* Header */}
			<MDBox
				display="flex"
				flexDirection="row"
				justifyContent="space-between"
				alignItems="center"
				px={3}
				py={1.5}
				sx={{ bgcolor: "grey.100", borderBottom: "1px solid", borderColor: "grey.300" }}
			>
				<MDBox display="flex" alignItems="center" gap={1}>
					<Icon color="info">swap_horiz</Icon>
					<MDTypography variant="h6" fontWeight="bold">
						Migration FAIQ → v3
					</MDTypography>
				</MDBox>
				{!migrating && (
					<MDButton variant="contained" color="info" size="small" onClick={handleClose}>
						Fermer
					</MDButton>
				)}
			</MDBox>

			<DialogContent sx={{ pt: 2 }}>
				{/* Step 1 — Source */}
				<MDTypography variant="subtitle2" fontWeight="bold" color="dark" mb={1}>
					1. Assistant source
				</MDTypography>
				<AccountAssistantPicker
					label="Source"
					clients={clients}
					accountValue={sourceAccount}
					assistantValue={sourceAssistant}
					assistants={sourceAssistants}
					disabled={migrating}
					onAccountChange={id => {
						setSourceAccount(id);
						setSourceAssistant("");
						setSourceAssistants([]);
						loadAssistants(id, setSourceAssistants);
					}}
					onAssistantChange={setSourceAssistant}
				/>

				<Divider sx={{ my: 2 }} />

				{/* Step 2 — Target */}
				<MDTypography variant="subtitle2" fontWeight="bold" color="dark" mb={1}>
					2. Assistant cible
				</MDTypography>
				<AccountAssistantPicker
					label="Cible"
					clients={clients}
					accountValue={targetAccount}
					assistantValue={targetAssistant}
					assistants={targetAssistants}
					disabled={migrating}
					onAccountChange={id => {
						setTargetAccount(id);
						setTargetAssistant("");
						setTargetAssistants([]);
						loadAssistants(id, setTargetAssistants);
					}}
					onAssistantChange={setTargetAssistant}
				/>

				<Divider sx={{ my: 2 }} />

				{/* Launch button */}
				{!migrating && !migrationDone && !migrationError && (
					<MDBox display="flex" justifyContent="center">
						<MDButton
							variant="gradient"
							color="info"
							disabled={!canLaunch}
							onClick={launchMigration}
						>
							<Icon sx={{ mr: 0.5 }}>rocket_launch</Icon>
							Lancer la migration
						</MDButton>
					</MDBox>
				)}

				{/* Progress stepper */}
				{(migrating || migrationDone || migrationError || Object.keys(stepStatuses).length > 0) && (
					<MDBox mt={2}>
						<MDBox display="flex" flexDirection="column" gap={0}>
							{MIGRATION_STEPS.map(({ key, label }, index) => {
								const s = stepStatuses[key];
								const isRunning = s?.status === "running";
								const isDone = s?.status === "done";
								const isError = s?.status === "error";
								const isLast = index === MIGRATION_STEPS.length - 1;

								let circleBg = "grey.300";
								if (isDone) circleBg = "success.main";
								else if (isError) circleBg = "error.main";
								else if (isRunning) circleBg = "info.main";

								let labelColor = "text.secondary";
								if (isDone) labelColor = "success.dark";
								else if (isError) labelColor = "error.main";
								else if (isRunning) labelColor = "info.main";

								let circleContent = (
									<MDBox sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "grey.500" }} />
								);
								if (isRunning) {
									circleContent = <CircularProgress size={14} sx={{ color: "#fff" }} />;
								} else if (isDone) {
									circleContent = (
										<Icon sx={{ fontSize: "14px !important", lineHeight: 1 }}>check</Icon>
									);
								} else if (isError) {
									circleContent = (
										<Icon sx={{ fontSize: "14px !important", lineHeight: 1 }}>close</Icon>
									);
								}

								return (
									<MDBox key={key} display="flex" flexDirection="row" alignItems="stretch">
										{/* Timeline column */}
										<MDBox
											display="flex"
											flexDirection="column"
											alignItems="center"
											sx={{ width: 32, flexShrink: 0 }}
										>
											<MDBox
												display="flex"
												alignItems="center"
												justifyContent="center"
												sx={{
													width: 28,
													height: 28,
													borderRadius: "50%",
													flexShrink: 0,
													bgcolor: circleBg,
													color: "#fff",
													fontSize: 14,
													transition: "background-color 0.3s"
												}}
											>
												{circleContent}
											</MDBox>
											{!isLast && (
												<MDBox
													sx={{
														width: 2,
														flex: 1,
														minHeight: 16,
														bgcolor: isDone ? "success.main" : "grey.300",
														transition: "background-color 0.3s",
														my: "2px"
													}}
												/>
											)}
										</MDBox>

										{/* Label */}
										<MDBox px={1.5} py={0.5} pb={isLast ? 0 : 1.5}>
											<MDTypography
												variant="body2"
												fontWeight={isRunning ? "bold" : "regular"}
												sx={{ color: labelColor }}
											>
												{s?.label ?? label}
											</MDTypography>
										</MDBox>
									</MDBox>
								);
							})}
						</MDBox>

						{migrationDone && (
							<MDBox
								mt={2}
								p={2}
								borderRadius="lg"
								display="flex"
								alignItems="center"
								gap={1}
								sx={{ bgcolor: "success.light" }}
							>
								<Icon color="success">check_circle</Icon>
								<MDTypography variant="body2" fontWeight="bold">
									Migration terminée avec succès.
								</MDTypography>
							</MDBox>
						)}

						{migrationError && (
							<MDBox
								mt={2}
								p={2}
								borderRadius="lg"
								display="flex"
								alignItems="center"
								gap={1}
								sx={{ bgcolor: "error.light" }}
							>
								<Icon color="error">error</Icon>
								<MDTypography variant="body2" fontWeight="bold">
									{migrationError}
								</MDTypography>
							</MDBox>
						)}

						{(migrationDone || migrationError) && (
							<MDBox display="flex" justifyContent="center" mt={2}>
								<MDButton variant="outlined" color="dark" onClick={resetState}>
									Nouvelle migration
								</MDButton>
							</MDBox>
						)}
					</MDBox>
				)}
			</DialogContent>
		</Dialog>
	);
};

export default FAIQMigrationDialog;
